import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  type NodeTypes,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Crosshair, Maximize2, Minimize2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import type { FullAlignmentNode } from "@/hooks/use-full-alignment";
import { ObjectiveNode } from "./objective-node";
import { StrategicNode } from "./strategic-node";
import { KrNode } from "./kr-node";
import { buildFlowLayout, collectExpandableIds, findNode } from "./layout";
import { NodeDetailDialog } from "./node-detail-dialog";

const nodeTypes: NodeTypes = {
  objective: ObjectiveNode,
  strategic: StrategicNode,
  kr: KrNode,
};

interface AlignmentGraphProps {
  roots: FullAlignmentNode[];
  year?: number;
}

const COLLEGE_NODE_TYPES = new Set(["college_strategic_pillar", "college_goal"]);

export function AlignmentGraph({ roots, year }: AlignmentGraphProps) {
  const navigate = useNavigate();
  const reactFlowRef = useRef<{ fitView: (options?: { padding?: number }) => void } | null>(null);

  const [expandedIds, setExpandedIds] = useState<Set<string>>(() =>
    collectExpandableIds(roots),
  );
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [focusNodeId, setFocusNodeId] = useState<string | null>(null);
  const [detailNode, setDetailNode] = useState<FullAlignmentNode | null>(null);

  // Compute effective roots based on focus
  const effectiveRoots = useMemo(() => {
    if (!focusNodeId) return roots;
    const node = findNode(roots, focusNodeId);
    return node ? [node] : roots;
  }, [roots, focusNodeId]);

  const focusedNodeTitle = useMemo(() => {
    if (!focusNodeId) return null;
    const node = findNode(roots, focusNodeId);
    return node?.title ?? null;
  }, [roots, focusNodeId]);

  const onToggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const onNavigate = useCallback(
    (id: string) => {
      void navigate({ to: "/objectives/$id", params: { id } });
    },
    [navigate],
  );

  const onFocusNode = useCallback((id: string) => {
    setFocusNodeId(id);
    const node = findNode(roots, id);
    if (node) {
      setExpandedIds(collectExpandableIds([node]));
    }
  }, [roots]);

  const clearFocus = useCallback(() => {
    setFocusNodeId(null);
    setExpandedIds(collectExpandableIds(roots));
  }, [roots]);

  const onShowDetail = useCallback((id: string) => {
    const node = findNode(roots, id);
    if (node) setDetailNode(node);
  }, [roots]);

  const { nodes: layoutNodes, edges: layoutEdges } = useMemo(
    () => buildFlowLayout(effectiveRoots, expandedIds, onToggleExpand, onNavigate, onFocusNode, onShowDetail, year),
    [effectiveRoots, expandedIds, onToggleExpand, onNavigate, onFocusNode, onShowDetail, year],
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(layoutNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(layoutEdges);

  // Sync layout when expandedIds or roots change
  useEffect(() => {
    setNodes(layoutNodes);
    setEdges(layoutEdges);
  }, [layoutNodes, layoutEdges, setNodes, setEdges]);

  // Fullscreen toggle
  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => !prev);
    setTimeout(() => {
      reactFlowRef.current?.fitView({ padding: 0.2 });
    }, 50);
  }, []);

  // Escape key exits fullscreen
  useEffect(() => {
    if (!isFullscreen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsFullscreen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isFullscreen]);

  // Prevent body scroll when fullscreen
  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [isFullscreen]);

  // Fit view when focus changes
  useEffect(() => {
    setTimeout(() => {
      reactFlowRef.current?.fitView({ padding: 0.2 });
    }, 50);
  }, [focusNodeId]);

  return (
    <div
      className={cn(
        "relative",
        isFullscreen
          ? "fixed inset-0 z-50 bg-[hsl(var(--background))]"
          : "h-[400px] rounded-lg border bg-[hsl(var(--card))] md:h-[600px]",
      )}
    >
      {/* Focus breadcrumb */}
      {focusNodeId && focusedNodeTitle && (
        <div className="absolute left-2 top-2 z-10 flex items-center gap-2 rounded-md border bg-[hsl(var(--card))] px-3 py-1.5 shadow-sm">
          <Crosshair className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--muted-foreground))]" />
          <span className="max-w-[200px] truncate text-sm font-medium">{focusedNodeTitle}</span>
          <button
            type="button"
            className="rounded p-0.5 text-[hsl(var(--muted-foreground))] transition-colors hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--foreground))]"
            onClick={clearFocus}
            title="Clear focus"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Fullscreen toggle */}
      <Button
        variant="ghost"
        size="icon"
        className="absolute right-2 top-2 z-10 h-8 w-8"
        onClick={toggleFullscreen}
        title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
      >
        {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
      </Button>

      <ReactFlow
        onInit={(instance) => { reactFlowRef.current = instance; }}
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.1}
        maxZoom={1.5}
        proOptions={{ hideAttribution: true }}
      >
        <Background color="hsl(200, 18%, 85%)" gap={20} />
        <Controls
          showInteractive={false}
          className="!bg-[hsl(var(--card))] !border-[hsl(var(--border))] !shadow-sm [&>button]:!bg-[hsl(var(--card))] [&>button]:!border-[hsl(var(--border))] [&>button:hover]:!bg-[hsl(var(--muted))]"
        />
        <MiniMap
          nodeColor={(node) => {
            const d = node.data as Record<string, unknown>;
            const nodeType = d.nodeType as string | undefined;
            if (nodeType) {
              return COLLEGE_NODE_TYPES.has(nodeType)
                ? "hsl(40, 63%, 48%)"
                : "hsl(213, 52%, 63%)";
            }
            // KR node
            if (d.krId) {
              return "hsl(200, 18%, 56%)"; // muted for KRs
            }
            // Objective node
            const status = d.status as string | undefined;
            switch (status) {
              case "completed":
                return "hsl(40, 63%, 48%)";
              case "in_progress":
                return "hsl(213, 52%, 63%)";
              case "killed":
                return "hsl(4, 64%, 46%)";
              case "draft":
                return "hsl(200, 18%, 73%)";
              default:
                return "hsl(228, 65%, 19%)";
            }
          }}
          className="!bg-[hsl(var(--muted))]"
        />
      </ReactFlow>

      <NodeDetailDialog
        node={detailNode}
        open={detailNode !== null}
        onOpenChange={(open) => { if (!open) setDetailNode(null); }}
        year={year}
      />
    </div>
  );
}
